// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson <hi@ben.gy>
// Additional permissions under AGPL-3.0 section 7 apply — see ADDITIONAL-TERMS.md.

/**
 * Search-as-you-type over Hugging Face's public model index, so a repo id can
 * be found rather than typed exactly — which on a phone keyboard is most of the
 * work of using this tool.
 *
 * Same host, same anonymous credential-less GET as the repo listing in
 * gguf/source.ts. The one thing that must not go wrong: HF's `/api/` rate limit
 * is a shared fixed window (500 requests per 300 s per IP) that the repo
 * listing draws from too, so a runaway typeahead would take the tool's actual
 * job down with it. Hence the cache, and hence `disabled` — one refusal and
 * suggestions stay off for the life of the page rather than retrying into the
 * wall. Debouncing is the caller's half of that bargain.
 *
 * A failed suggestion is always silent: this module never throws (except the
 * caller's own abort) and never surfaces an error box.
 */

export interface ModelHit {
  /** `owner/name`, exactly what the address field wants. */
  id: string;
  downloads: number;
}

/** How many suggestions to ask for — enough to be useful, short enough to scan. */
const LIMIT = 8;

/** Queries already answered this session, keyed on the lowercased query. */
const cache = new Map<string, ModelHit[]>();
const MAX_CACHE = 60;

let disabled = false;
let disabledReason = '';

/**
 * Is the value worth suggesting against? Pure, so the debounce logic above it
 * stays testable without a network. Anything that is already a complete address
 * — a URL, an `hf.co/…` link, a `.gguf` path — has nothing left to suggest.
 */
export function shouldSuggest(raw: string): boolean {
  const q = raw.trim();
  if (q.length < 2) return false;
  if (q.includes('://')) return false;
  if (q.toLowerCase().startsWith('hf.co/')) return false;
  if (q.toLowerCase().endsWith('.gguf')) return false;
  return true;
}

/**
 * The search endpoint. `expand[]=downloads` is not an optimisation detail: the
 * default response carries every repo's full tag array, which is ~18× the bytes
 * for nothing we render.
 */
export function searchUrl(query: string): string {
  return (
    `https://huggingface.co/api/models?search=${encodeURIComponent(query.trim())}` +
    `&filter=gguf&limit=${LIMIT}&sort=downloads&direction=-1&expand[]=downloads`
  );
}

/** True once Hugging Face has refused a search — suggestions are off for good. */
export function isSuggestDisabled(): boolean {
  return disabled;
}

/**
 * What the refusal actually was, for the line the user reads. Any non-ok status
 * latches `disabled`, but only 429 is a rate limit — saying "rate-limited" after
 * a transient 503 sends people to look for a problem they do not have.
 */
export function suggestDisabledReason(): string {
  return disabledReason;
}

/**
 * Look up GGUF repos matching `query`. Returns `[]` for anything that goes
 * wrong; only an abort propagates, so the caller can tell a cancelled keystroke
 * from an empty result.
 */
export async function searchGgufRepos(query: string, signal?: AbortSignal): Promise<ModelHit[]> {
  const key = query.trim().toLowerCase();
  if (!key || disabled) return [];
  const hit = cache.get(key);
  if (hit) return hit;

  let res: Response;
  try {
    res = await fetch(searchUrl(query), { signal, credentials: 'omit', referrerPolicy: 'no-referrer' });
  } catch (err) {
    if ((err as Error)?.name === 'AbortError') throw err;
    return [];
  }
  if (!res.ok) {
    // 429 is the one that matters, but any refusal is reason enough to stop
    // spending a budget the repo listing needs.
    disabled = true;
    disabledReason =
      res.status === 429
        ? 'Hugging Face rate-limited the model search'
        : `Hugging Face refused the model search (HTTP ${res.status})`;
    return [];
  }

  let items: Array<{ id?: string; downloads?: number }>;
  try {
    items = (await res.json()) as Array<{ id?: string; downloads?: number }>;
  } catch {
    return [];
  }

  const out: ModelHit[] = [];
  for (const it of items) {
    if (typeof it.id !== 'string' || !it.id.includes('/')) continue;
    out.push({ id: it.id, downloads: typeof it.downloads === 'number' ? it.downloads : 0 });
  }
  if (cache.size >= MAX_CACHE) cache.delete(cache.keys().next().value!);
  cache.set(key, out);
  return out;
}
